/**
 * Copyright (c) 2025 Tj L Collins, DaddySavesTheWorld, and Mythbound Adventure Studio
 * All Rights Reserved
 *
 * @fileoverview Session lifecycle controls component
 * @version 1.0.0
 * @since v0.0.07
 * @created 2025-12-16
 * @author Agent B (Gameplay/Session/Notifications)
 *
 * Purpose: Session management UI with lifecycle controls, auto-save indicator,
 * and participant status display.
 *
 * CONTRACT REFERENCE:
 * - shared/contracts/session/v1/session-management.yaml
 *
 * FEATURES:
 * - Session state display (Active/Paused/Ended)
 * - Session duration timer (server-timed, PRD001A 001A.5 — excludes paused time)
 * - Participant count and status
 * - Control buttons: Start/End are GM-only; Pause/Resume are driven by the
 *   server's caller-specific `can_pause`/`can_resume` flags (PRD001A 001A.5,
 *   item 2) — ANY participant may pause, and only the recorded pauser or the
 *   GM may resume. Never re-derived from `isGM` for Pause/Resume specifically.
 * - Emergency Save button - all players
 * - Auto-save indicator (subtle icon pulse, no toast)
 * - Participant list with disconnect indicators
 * - Grace period countdown for disconnected players
 *
 * AUTO-SAVE INDICATOR:
 * - Cloud icon in header area
 * - Pulses with glow for 1s on save
 * - No text, no toast, no sound
 * - Accessible: aria-label="Saving..." during pulse
 */

import React, { useState, useEffect, useCallback, useMemo, useLayoutEffect, useRef } from 'react';
import { webSocketService } from '../../services/websocket/websocketService';
import { UserDisplay } from '../UserDisplay';
import { useSession } from '../../context/SessionContext';
import { ApiError } from '../../services/api/ApiError';
import { getSessionErrorCode, getSessionErrorMessage, sessionService } from '../../services/api/SessionService';
import { useOptionalCampaignRealtime } from '../../context/CampaignRealtimeContext';

// =============================================================================
// TYPES
// =============================================================================

export type SessionState = 'scheduled' | 'active' | 'paused' | 'ended';

export interface Participant {
  screenName: string;
  screenNameId: string;
  characterName?: string;
  role: 'player' | 'gm';
  connected: boolean;
  disconnectedAt?: Date;
}

export interface SessionControlsProps {
  /** Session ID */
  sessionId: string;
  /** Legacy account-wide hint; campaign controls use the current membership response instead. */
  isGM: boolean;
  /** Navigate to the persisted session created by a successful campaign start. */
  onSessionStarted?: (sessionId: string) => void;
  /** Current participant ID (for emergency save) */
  participantId: string;
  /** Callback when session state changes */
  onSessionStateChange?: (newState: SessionState) => void;
  /** Callback on emergency save */
  onEmergencySave?: (result: { success: boolean; message: string }) => void;
  /** Optional CSS class */
  className?: string;
  /** Compact mode */
  compact?: boolean;
}

// =============================================================================
// CONSTANTS
// =============================================================================

/** Grace period for disconnect (seconds) */
const DISCONNECT_GRACE_SECONDS = 60;

/** Emergency save cooldown (seconds) */
const EMERGENCY_SAVE_COOLDOWN_SECONDS = 120;

function getEmergencySaveThrottleSeconds(error: unknown): number {
  if (!(error instanceof ApiError) || error.status !== 429 || !error.detail || typeof error.detail !== 'object') {
    return 0;
  }
  const outer = error.detail as Record<string, unknown>;
  const detail = outer.detail && typeof outer.detail === 'object'
    ? outer.detail as Record<string, unknown>
    : outer;
  const seconds = detail.throttle_remaining_seconds;
  return typeof seconds === 'number' && Number.isFinite(seconds) && seconds > 0 ? Math.ceil(seconds) : 0;
}

/**
 * Session state labels.
 *
 * Pause is its own persisted lifecycle status. Master Mode is a separate
 * explicit session setting surfaced in VoicePanel.
 */
const STATE_LABELS: Record<SessionState, { label: string; color: string; icon: string }> = {
  scheduled: { label: 'Scheduled', color: '#6b7280', icon: '📅' },
  active: { label: 'Active', color: '#22c55e', icon: '▶️' },
  paused: { label: 'Paused', color: '#eab308', icon: '⏸️' },
  ended: { label: 'Ended', color: '#ef4444', icon: '⏹️' }
};

// =============================================================================
// COMPONENT
// =============================================================================

export const SessionControls: React.FC<SessionControlsProps> = ({
  sessionId,
  participantId,
  onSessionStarted,
  onSessionStateChange,
  onEmergencySave,
  className = '',
  compact = false
}) => {
  // Session state
  const [sessionState, setSessionState] = useState<SessionState>('scheduled');
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  
  // Auto-save state
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaveTime, setLastSaveTime] = useState<Date | null>(null);
  
  // Emergency save throttle and request state
  const [emergencySaveCooldown, setEmergencySaveCooldown] = useState(0);
  const [isEmergencySavePending, setIsEmergencySavePending] = useState(false);
  
  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Disconnect timer tracking (for UI countdown)
  const [disconnectTimers, setDisconnectTimers] = useState<Record<string, number>>({});

  // Use SessionContext for HTTP pause/resume (replaces WebSocket fire-and-forget sends).
  // session_id is available from the active session object in context.
  // WHY context over direct service call: context also unconditionally
  // refreshes state after pause/resume (PRD001A R5a — see `forceRefresh` in
  // SessionContext.tsx), keeping the context session object in sync with the
  // server response even when two completed actions land close together.
  //
  // `sessionState: contextSessionState` (renamed to avoid colliding with this
  // component's own local `sessionState` display string above) is the polled
  // GET /api/sessions/{id}/state response — the ONLY source for `can_pause`/
  // `can_resume`/`paused_by_me` and the pause-excluded `elapsed_seconds`
  // (PRD001A 001A.5, items 1/2/5).
  const campaignRealtime = useOptionalCampaignRealtime();
  const {
    session: contextSession,
    sessionState: contextSessionState,
    status: contextStatus,
    message: contextMessage,
    startSession: contextStart,
    endSession: contextEnd,
    pauseSession: contextPause,
    resumeSession: contextResume,
  } = useSession();

  const contextSessionId = contextSession?.session_id ?? null;
  const currentContextSessionIdRef = useRef<string | null>(contextSessionId);
  currentContextSessionIdRef.current = contextSessionId;
  const lifecycleActionInFlightRef = useRef(false);
  const lifecycleRequestGenerationRef = useRef(0);
  const lifecycleContextGenerationRef = useRef(0);
  const previousLifecycleContextSessionIdRef = useRef<string | null>(contextSessionId);
  const lifecycleRouteIdentityRef = useRef({ sessionId, participantId });
  const lifecycleComponentMountedRef = useRef(false);
  const currentEmergencySaveSessionIdRef = useRef<string | null>(contextSessionId);
  currentEmergencySaveSessionIdRef.current = contextSessionId;
  const previousEmergencySaveSessionIdRef = useRef<string | null>(contextSessionId);
  const emergencySaveRequestGenerationRef = useRef(0);
  const emergencySaveRequestInFlightRef = useRef(false);
  const emergencySaveAbortControllerRef = useRef<AbortController | null>(null);

  useLayoutEffect(() => {
    lifecycleComponentMountedRef.current = true;
    return () => {
      lifecycleComponentMountedRef.current = false;
      lifecycleRequestGenerationRef.current += 1;
      lifecycleActionInFlightRef.current = false;
    };
  }, []);

  useLayoutEffect(() => {
    const previousIdentity = lifecycleRouteIdentityRef.current;
    if (previousIdentity.sessionId === sessionId && previousIdentity.participantId === participantId) return;
    lifecycleRouteIdentityRef.current = { sessionId, participantId };
    lifecycleRequestGenerationRef.current += 1;
    lifecycleActionInFlightRef.current = false;
    setIsLoading(false);
    setError(null);
  }, [sessionId, participantId]);

  useLayoutEffect(() => {
    if (previousLifecycleContextSessionIdRef.current === contextSessionId) return;
    previousLifecycleContextSessionIdRef.current = contextSessionId;
    lifecycleContextGenerationRef.current += 1;
  }, [contextSessionId]);

  const isCurrentLifecycleRouteRequest = (
    requestGeneration: number,
    requestedRouteSessionId: string,
    requestedParticipantId: string,
  ) => lifecycleComponentMountedRef.current &&
    lifecycleRequestGenerationRef.current === requestGeneration &&
    sessionId === requestedRouteSessionId &&
    participantId === requestedParticipantId;

  useLayoutEffect(() => {
    if (previousEmergencySaveSessionIdRef.current === contextSessionId) return;
    previousEmergencySaveSessionIdRef.current = contextSessionId;
    emergencySaveRequestGenerationRef.current += 1;
    emergencySaveRequestInFlightRef.current = false;
    emergencySaveAbortControllerRef.current?.abort();
    emergencySaveAbortControllerRef.current = null;
    setIsEmergencySavePending(false);
    setEmergencySaveCooldown(0);
  }, [contextSessionId]);

  useLayoutEffect(() => () => {
    emergencySaveRequestGenerationRef.current += 1;
    emergencySaveRequestInFlightRef.current = false;
    emergencySaveAbortControllerRef.current?.abort();
    emergencySaveAbortControllerRef.current = null;
  }, []);

  // ==========================================================================
  // POLLING OUTAGE HANDLING (PRD001A R5b)
  // ==========================================================================
  //
  // `contextSessionState.can_pause`/`can_resume` are NOT cleared when a poll
  // fails — `SessionContext.fetchState` deliberately keeps the prior
  // snapshot so a transient blip doesn't fabricate an ended session. But
  // that means the LAST-KNOWN flags can go stale: if the most recent poll
  // actually failed, `can_pause`/`can_resume` still reflect whatever the
  // last SUCCESSFUL poll saw, not "we don't currently know." Gating Pause/
  // Resume on the flags alone (as before) left them enabled through an
  // outage with nothing telling the user validation had stopped working —
  // the exact release-review finding.
  //
  // `contextStatus` distinguishes a genuinely failed last fetch
  // (`'unavailable'`/`'forbidden'`/`'not_found'`/`'error'`) from every other
  // state, including a mid-mount `undefined`/`'idle'`/`'loading'`/`'ready'`/
  // `'empty'` — `undefined` in particular covers callers (existing tests)
  // that mock `useSession()` without a `status` field at all, so this never
  // blocks controls for a caller that hasn't opted into reporting status.
  // Once the poll succeeds again (automatic — the 3s interval keeps trying),
  // `fetchState` sets `status` back to `'ready'` and controls re-enable with
  // the freshly revalidated flags; no separate manual retry is needed here
  // (compare `ServiceUnavailable`, used where a mount-time fetch can fail
  // with nothing to poll yet).
  const isSessionStateUnavailable =
    contextStatus === 'unavailable' ||
    contextStatus === 'forbidden' ||
    contextStatus === 'not_found' ||
    contextStatus === 'error';

  // ==========================================================================
  // COMPUTED VALUES
  // ==========================================================================

  const stateInfo = useMemo(() => STATE_LABELS[sessionState], [sessionState]);

  const connectedCount = useMemo(() =>
    participants.filter(p => p.connected).length,
    [participants]
  );

  const lifecycleContextReady =
    contextStatus === 'ready' &&
    Boolean(contextSession && contextSessionState) &&
    contextSession?.session_id === sessionId &&
    contextSession?.session_id === contextSessionState?.session_id &&
    contextSession?.campaign_id === contextSessionState?.campaign_id;
  const isCampaignGM = Boolean(
    participantId &&
    contextSessionState?.participants?.some((participant) =>
      participant.user_id === participantId && participant.role === 'gm'
    )
  );
  // Lifecycle authorization follows the current campaign membership returned
  // by the authenticated session-state endpoint. The account-wide role prop
  // cannot grant campaign GM controls.
  const canStart =
    lifecycleContextReady && isCampaignGM &&
    contextSession?.status === 'scheduled' && contextSessionState?.status === 'scheduled' &&
    sessionState === 'scheduled';
  // Pause/Resume (PRD001A 001A.5, item 2): driven ENTIRELY by the server's
  // caller-specific flags — never re-derived from `isGM` or any other
  // client-side role guess. Any participant may pause; only the recorded
  // pauser or the GM may resume, and the server is the one deciding which
  // of those the caller is. Defaults to `false` (no button) until the
  // context has polled a real state, which is the safe default — nobody
  // sees an enabled control before the server has actually said so.
  // Blocked outright while the last poll is known to have failed (R5b) —
  // an outage never leaves a stale "yes" flag actionable.
  const pauseContextMatchesRoute = contextSessionId === sessionId && contextSessionState?.session_id === sessionId;
  const canPause = pauseContextMatchesRoute && !isSessionStateUnavailable && contextSessionState?.can_pause === true;
  const canResume = pauseContextMatchesRoute && !isSessionStateUnavailable && contextSessionState?.can_resume === true;
  const canEnd =
    lifecycleContextReady && isCampaignGM &&
    (contextSessionState?.status === 'active' || contextSessionState?.status === 'paused');
  const emergencySaveContextReady =
    contextStatus === 'ready' &&
    Boolean(contextSessionId) &&
    contextSessionState?.session_id === contextSessionId &&
    (contextSessionState?.status === 'active' || contextSessionState?.status === 'paused');
  const canEmergencySave =
    emergencySaveContextReady && emergencySaveCooldown === 0 && !isEmergencySavePending;

  // ==========================================================================
  // FORMAT HELPERS
  // ==========================================================================
  
  const formatDuration = (seconds: number): string => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // ==========================================================================
  // WEBSOCKET HANDLERS
  // ==========================================================================
  
  /**
   * Handle session state updates from server (stable callback)
   */
  const handleSessionUpdate = useCallback((data: any) => {
    const newState = data.state as SessionState;
    setSessionState(newState);
    onSessionStateChange?.(newState);
  }, [onSessionStateChange]);
  
  /**
   * Handle auto-save completion (stable callback)
   */
  const handleAutoSaveComplete = useCallback(() => {
    setIsSaving(true);
    setLastSaveTime(new Date());
    
    // Reset saving indicator after 1 second (per spec: 1s pulse)
    setTimeout(() => setIsSaving(false), 1000);
  }, []);
  
  /**
   * Handle participant updates (stable callback)
   */
  const handleParticipantUpdate = useCallback((data: any) => {
    const { participant_id, action, screen_name } = data;
    
    setParticipants(prev => {
      const updated = [...prev];
      const idx = updated.findIndex(p => p.screenNameId === participant_id);
      
      if (action === 'connected' || action === 'reconnected') {
        if (idx >= 0) {
          updated[idx] = { ...updated[idx], connected: true, disconnectedAt: undefined };
        }
        // Clear disconnect timer
        setDisconnectTimers(timers => {
          const newTimers = { ...timers };
          delete newTimers[participant_id];
          return newTimers;
        });
      } else if (action === 'disconnected') {
        if (idx >= 0) {
          updated[idx] = { ...updated[idx], connected: false, disconnectedAt: new Date() };
        }
        // Start disconnect timer for UI
        setDisconnectTimers(timers => ({
          ...timers,
          [participant_id]: DISCONNECT_GRACE_SECONDS
        }));
      }
      
      return updated;
    });
  }, []);

  // ==========================================================================
  // PERSISTED STATE SYNC (PRD001A R5)
  // ==========================================================================

  /**
   * Sync this component's local `sessionState` display value from
   * `SessionContext`'s polled/seeded `contextSessionState.status`.
   *
   * Previously `sessionState` only ever changed via the WebSocket
   * `session_update` handler below, so it stayed at its hardcoded
   * `'scheduled'` default until a WebSocket message happened to arrive —
   * which meant a direct `/session/:id` link, or an ordinary page refresh,
   * showed no Start/End controls, no duration timer, and stale
   * canStart/canEnd gating regardless of the session's real persisted
   * status, even once `SessionContext` had already loaded it (R5 finding:
   * "controls and timer use persisted state/permissions after reload").
   * `contextSessionState` is now seeded from the real session identity on
   * mount and re-polled every 3s (see `SessionContext.tsx`'s initialization
   * effect), so this keeps the display state in sync independent of
   * WebSocket delivery; the WebSocket handler below still applies for
   * faster-than-3s updates and is left unchanged.
   *
   * The canonical paused state remains separate from Master Mode.
   */
  useEffect(() => {
    if (!contextSessionState) return;
    setSessionState(
      contextSessionState.status
    );
  }, [contextSessionState]);

  // ==========================================================================
  // SUBSCRIPTIONS
  // ==========================================================================

  useEffect(() => {
    webSocketService.subscribe('session_update', handleSessionUpdate);
    webSocketService.subscribe('auto_save_complete', handleAutoSaveComplete);
    webSocketService.subscribe('participant_update', handleParticipantUpdate);
    
    return () => {
      webSocketService.unsubscribe('session_update', handleSessionUpdate);
      webSocketService.unsubscribe('auto_save_complete', handleAutoSaveComplete);
      webSocketService.unsubscribe('participant_update', handleParticipantUpdate);
    };
  }, [handleSessionUpdate, handleAutoSaveComplete, handleParticipantUpdate]);

  // ==========================================================================
  // TIMERS
  // ==========================================================================
  
  // Session duration timer (PRD001A 001A.5, item 5).
  //
  // Uses the server's `elapsed_seconds` from the polled session state — that
  // value already excludes every paused interval
  // (`Session.elapsed_seconds_excluding_pauses()` on the backend); this
  // effect NEVER computes elapsed time itself from a locally-tracked start
  // timestamp, which would otherwise keep counting through a pause.
  //
  // Between 3-second polling ticks, the display counts up locally from that
  // server baseline ONLY while `status === 'active'` — the moment the poll
  // reports `'paused'` (or `'ended'`/`'scheduled'`), the interval is not
  // (re)started, so the displayed value freezes at the last server value
  // instead of drifting through the pause.
  useEffect(() => {
    if (!contextSessionState) return;
    const baseline = contextSessionState.elapsed_seconds ?? 0;
    setElapsedSeconds(baseline);
    if (contextSessionState.status !== 'active') return;

    const tickStartedAt = Date.now();
    const interval = setInterval(() => {
      setElapsedSeconds(baseline + Math.floor((Date.now() - tickStartedAt) / 1000));
    }, 1000);

    return () => clearInterval(interval);
  }, [contextSessionState]);
  
  // Emergency save cooldown timer
  useEffect(() => {
    if (emergencySaveCooldown <= 0) return;
    
    const interval = setInterval(() => {
      setEmergencySaveCooldown(prev => Math.max(0, prev - 1));
    }, 1000);
    
    return () => clearInterval(interval);
  }, [emergencySaveCooldown]);
  
  // Disconnect grace period countdowns
  useEffect(() => {
    const ids = Object.keys(disconnectTimers);
    if (ids.length === 0) return;
    
    const interval = setInterval(() => {
      setDisconnectTimers(prev => {
        const updated: Record<string, number> = {};
        for (const id of Object.keys(prev)) {
          if (prev[id] > 0) {
            updated[id] = prev[id] - 1;
          }
        }
        return updated;
      });
    }, 1000);
    
    return () => clearInterval(interval);
  }, [disconnectTimers]);

  // ==========================================================================
  // ACTIONS
  // ==========================================================================
  
  const handleStartSession = async () => {
    if (!canStart || !contextSession?.campaign_id || lifecycleActionInFlightRef.current) return;
    lifecycleActionInFlightRef.current = true;
    const requestGeneration = lifecycleRequestGenerationRef.current;
    const requestedRouteSessionId = sessionId;
    const requestedParticipantId = participantId;
    const requestedContextSessionId = contextSessionId;
    const requestedContextGeneration = lifecycleContextGenerationRef.current;
    setIsLoading(true);
    setError(null);
    try {
      // The backend creates the persisted session by campaign ID and derives
      // the actor from authenticated credentials. SessionContext only resolves
      // after a validated HTTP acknowledgment, never a queued WebSocket send.
      const started = await contextStart(contextSession.campaign_id, 'webapp');
      const contextStillMatchesStart =
        (lifecycleContextGenerationRef.current === requestedContextGeneration &&
          currentContextSessionIdRef.current === requestedContextSessionId) ||
        (lifecycleContextGenerationRef.current === requestedContextGeneration + 1 &&
          currentContextSessionIdRef.current === started.session_id);
      if (!isCurrentLifecycleRouteRequest(requestGeneration, requestedRouteSessionId, requestedParticipantId) || !contextStillMatchesStart) return;
      setSessionState('active');
      onSessionStateChange?.('active');
      onSessionStarted?.(started.session_id);
    } catch (err) {
      if (isCurrentLifecycleRouteRequest(requestGeneration, requestedRouteSessionId, requestedParticipantId) && !(err instanceof Error && err.name === 'AbortError')) {
        if (getSessionErrorCode(err) === 'stale_state') campaignRealtime?.refreshSnapshot();
        setError(getSessionErrorMessage(err, 'Failed to start session'));
      }
    } finally {
      if (isCurrentLifecycleRouteRequest(requestGeneration, requestedRouteSessionId, requestedParticipantId)) {
        lifecycleActionInFlightRef.current = false;
        setIsLoading(false);
      }
    }
  };
  
  const handlePauseState = async (kind: 'pause' | 'resume') => {
    if (!(kind === 'pause' ? canPause : canResume) || lifecycleActionInFlightRef.current) return;
    if (!contextSessionId || contextSessionId !== sessionId) return;
    lifecycleActionInFlightRef.current = true;
    const requestGeneration = lifecycleRequestGenerationRef.current;
    const requestedContextGeneration = lifecycleContextGenerationRef.current;
    const requestedRouteSessionId = sessionId;
    const requestedParticipantId = participantId;
    const isCurrent = () => isCurrentLifecycleRouteRequest(requestGeneration, requestedRouteSessionId, requestedParticipantId) &&
      lifecycleContextGenerationRef.current === requestedContextGeneration &&
      currentContextSessionIdRef.current === requestedRouteSessionId;
    setIsLoading(true);
    setError(null);
    try {
      await (kind === 'pause' ? contextPause : contextResume)(contextSessionId);
    } catch (err) {
      if (isCurrent() && !(err instanceof Error && err.name === 'AbortError')) {
        setError(getSessionErrorMessage(err, `Failed to ${kind} session`));
      }
    } finally {
      if (isCurrent()) {
        lifecycleActionInFlightRef.current = false;
        setIsLoading(false);
      }
    }
  };
  const handlePauseSession = () => handlePauseState('pause');
  const handleResumeSession = () => handlePauseState('resume');

  const handleEndSession = async () => {
    if (!canEnd || lifecycleActionInFlightRef.current) return;
    lifecycleActionInFlightRef.current = true;
    const requestGeneration = lifecycleRequestGenerationRef.current;
    const requestedRouteSessionId = sessionId;
    const requestedParticipantId = participantId;
    const requestedContextSessionId = contextSessionId;
    const requestedContextGeneration = lifecycleContextGenerationRef.current;
    // Keep the existing explicit confirmation before issuing the persisted
    // end request. A cancelled dialog makes no lifecycle request.
    if (!window.confirm('Are you sure you want to end this session?')) {
      lifecycleActionInFlightRef.current = false;
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      await contextEnd();
      const contextStillMatchesEnd =
        lifecycleContextGenerationRef.current === requestedContextGeneration &&
        currentContextSessionIdRef.current === requestedContextSessionId;
      if (!isCurrentLifecycleRouteRequest(requestGeneration, requestedRouteSessionId, requestedParticipantId) || !contextStillMatchesEnd) return;
      setSessionState('ended');
      onSessionStateChange?.('ended');
    } catch (err) {
      if (isCurrentLifecycleRouteRequest(requestGeneration, requestedRouteSessionId, requestedParticipantId) && !(err instanceof Error && err.name === 'AbortError')) {
        setError(getSessionErrorMessage(err, 'Failed to end session'));
      }
    } finally {
      if (isCurrentLifecycleRouteRequest(requestGeneration, requestedRouteSessionId, requestedParticipantId)) {
        lifecycleActionInFlightRef.current = false;
        setIsLoading(false);
      }
    }
  };
  
  const handleEmergencySave = async () => {
    const requestedSessionId = contextSession?.session_id;
    if (!canEmergencySave || !requestedSessionId || emergencySaveRequestInFlightRef.current) return;

    emergencySaveRequestInFlightRef.current = true;
    const requestGeneration = ++emergencySaveRequestGenerationRef.current;
    const abortController = new AbortController();
    emergencySaveAbortControllerRef.current = abortController;
    setIsEmergencySavePending(true);
    setError(null);

    try {
      const result = await sessionService.emergencySave(requestedSessionId, participantId, { signal: abortController.signal });
      if (
        currentEmergencySaveSessionIdRef.current !== requestedSessionId ||
        emergencySaveRequestGenerationRef.current !== requestGeneration
      ) return;

      const savedAtConfirmed =
        result.success === true &&
        typeof result.saved_at === 'string' &&
        result.saved_at.trim().length > 0 &&
        Number.isFinite(Date.parse(result.saved_at));
      if (savedAtConfirmed) {
        setEmergencySaveCooldown(EMERGENCY_SAVE_COOLDOWN_SECONDS);
        onEmergencySave?.({ success: true, message: result.message || 'Emergency save completed' });
        return;
      }

      const throttleSeconds =
        typeof result.throttle_remaining_seconds === 'number' && result.throttle_remaining_seconds > 0
          ? Math.ceil(result.throttle_remaining_seconds)
          : 0;
      if (throttleSeconds > 0) setEmergencySaveCooldown(throttleSeconds);
      const message = result.message || result.error || (
        throttleSeconds > 0
          ? 'Emergency save is throttled. Try again in ' + throttleSeconds + ' seconds.'
          : 'Emergency save was not completed'
      );
      setError(message);
      onEmergencySave?.({ success: false, message });
    } catch (err) {
      if (
        currentEmergencySaveSessionIdRef.current !== requestedSessionId ||
        emergencySaveRequestGenerationRef.current !== requestGeneration
      ) return;

      const throttleSeconds = getEmergencySaveThrottleSeconds(err);
      if (throttleSeconds > 0) setEmergencySaveCooldown(throttleSeconds);
      const message = throttleSeconds > 0
        ? 'Emergency save is throttled. Try again in ' + throttleSeconds + ' seconds.'
        : getSessionErrorMessage(err, 'Failed to save session');
      setError(message);
      onEmergencySave?.({ success: false, message });
    } finally {
      if (
        currentEmergencySaveSessionIdRef.current === requestedSessionId &&
        emergencySaveRequestGenerationRef.current === requestGeneration
      ) {
        emergencySaveRequestInFlightRef.current = false;
        emergencySaveAbortControllerRef.current = null;
        setIsEmergencySavePending(false);
      }
    }
  };

  // ==========================================================================
  // RENDER
  // ==========================================================================

  return (
    <div className={`session-controls ${className}`} style={styles.container}>
      {/* Header with State & Auto-Save Indicator */}
      <div style={styles.header}>
        <div style={styles.stateDisplay}>
          <span style={{ ...styles.stateIcon, color: stateInfo.color }}>
            {stateInfo.icon}
          </span>
          {!compact && (
            <span style={{ ...styles.stateLabel, color: stateInfo.color }}>
              {stateInfo.label}
            </span>
          )}
        </div>
        
        {/* Auto-Save Indicator */}
        <div
          className={isSaving ? 'saving' : ''}
          style={{
            ...styles.autoSaveIndicator,
            ...(isSaving ? styles.autoSaveIndicatorSaving : {})
          }}
          aria-label={isSaving ? 'Saving...' : 'Auto-save indicator'}
          title={lastSaveTime ? `Last saved: ${lastSaveTime.toLocaleTimeString()}` : 'Auto-saves every 5 minutes'}
        >
          ☁️
        </div>
      </div>
      
      {/* CSS for save pulse animation */}
      <style>{saveAnimationStyles}</style>
      
      {/* Session Duration (active sessions only) */}
      {(sessionState === 'active' || sessionState === 'paused') && (
        <div style={styles.durationDisplay}>
          <span style={styles.durationLabel}>Duration:</span>
          <span style={styles.durationValue}>{formatDuration(elapsedSeconds)}</span>
        </div>
      )}
      
      {/* Participant Count */}
      <div style={styles.participantCount}>
        <span style={styles.participantIcon}>👥</span>
        <span>{connectedCount}/{participants.length} connected</span>
      </div>
      
      {/* Polling outage (PRD001A R5b): the last state fetch is known to have
          failed — honest, non-dismissible (it clears itself once a poll
          succeeds again), distinct from the local action-error banner below
          (`role="status"`, not `"alert"`, so it never collides with that
          banner's `getByRole('alert')` queries). */}
      {isSessionStateUnavailable && (
        <div style={styles.error} role="status">
          ⚠️ Session state unavailable{contextMessage ? `: ${contextMessage}` : ''}. Session controls are
          disabled until the connection is revalidated.
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div style={styles.error} role="alert">
          ❌ {error}
          <button onClick={() => setError(null)} style={styles.dismissError}>×</button>
        </div>
      )}
      
      {/* Control Buttons */}
      <div style={styles.controlButtons}>
        {/* GM Controls */}
        {canStart && (
          <button
            onClick={handleStartSession}
            disabled={isLoading}
            style={{ ...styles.button, ...styles.startButton }}
            aria-label="Start session"
          >
            ▶️ {!compact && 'Start'}
          </button>
        )}
        
        {canPause && (
          <button
            onClick={handlePauseSession}
            disabled={isLoading}
            style={{ ...styles.button, ...styles.pauseButton }}
            aria-label="Pause session"
          >
            ⏸️ {!compact && 'Pause'}
          </button>
        )}
        
        {canResume && (
          <button
            onClick={handleResumeSession}
            disabled={isLoading}
            style={{ ...styles.button, ...styles.resumeButton }}
            aria-label="Resume session"
          >
            ▶️ {!compact && 'Resume'}
          </button>
        )}
        
        {canEnd && (
          <button
            onClick={handleEndSession}
            disabled={isLoading}
            style={{ ...styles.button, ...styles.endButton }}
            aria-label="End session"
          >
            ⏹️ {!compact && 'End'}
          </button>
        )}
        
        {/* Emergency Save (all players) */}
        {(sessionState === 'active' || sessionState === 'paused') && (
          <button
            onClick={handleEmergencySave}
            disabled={isLoading || isEmergencySavePending || !canEmergencySave}
            style={{
              ...styles.button,
              ...styles.emergencySaveButton,
              opacity: canEmergencySave ? 1 : 0.5
            }}
            aria-label={canEmergencySave ? 'Emergency save' : `Emergency save (${emergencySaveCooldown}s cooldown)`}
            title={canEmergencySave ? 'Emergency save' : `Available in ${emergencySaveCooldown}s`}
          >
            💾 {!compact && (canEmergencySave ? 'Save' : `${emergencySaveCooldown}s`)}
          </button>
        )}
      </div>
      
      {/* Participant List */}
      {!compact && participants.length > 0 && (
        <div style={styles.participantList}>
          <h4 style={styles.participantListTitle}>Participants</h4>
          {participants.map((p) => (
            <div
              key={p.screenNameId}
              style={{
                ...styles.participantItem,
                opacity: p.connected ? 1 : 0.5
              }}
            >
              <span style={styles.participantRole}>
                {p.role === 'gm' ? '👑' : '🎭'}
              </span>
              <div style={styles.participantName}>
                {p.characterName ? (
                  <span>{p.characterName}</span>
                ) : (
                  <UserDisplay
                    screenName={p.screenName}
                    tag={p.screenNameId?.slice(-4) || ''}
                    showTagOnHover
                    textProps={{
                      fontSize: '13px',
                      color: 'rgba(255, 255, 255, 0.9)'
                    }}
                  />
                )}
              </div>
              {!p.connected && (
                <span style={styles.disconnectedBadge}>
                  {disconnectTimers[p.screenNameId] !== undefined
                    ? `⏳ ${disconnectTimers[p.screenNameId]}s`
                    : '❌ Disconnected'}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// =============================================================================
// STYLES
// =============================================================================

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    padding: '16px',
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    borderRadius: '12px',
    backdropFilter: 'blur(8px)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    fontFamily: '"Cinzel", serif',
    minWidth: '200px'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  stateDisplay: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  stateIcon: {
    fontSize: '18px'
  },
  stateLabel: {
    fontSize: '14px',
    fontWeight: '600'
  },
  autoSaveIndicator: {
    width: '24px',
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '16px',
    opacity: 0.6,
    transition: 'opacity 0.3s ease'
  },
  autoSaveIndicatorSaving: {
    opacity: 1,
    animation: 'save-pulse 1s ease-in-out'
  },
  durationDisplay: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '8px',
    padding: '8px',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderRadius: '6px'
  },
  durationLabel: {
    fontSize: '12px',
    color: 'rgba(255, 255, 255, 0.6)'
  },
  durationValue: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#fff',
    fontFamily: 'monospace'
  },
  participantCount: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
    color: 'rgba(255, 255, 255, 0.7)'
  },
  participantIcon: {
    fontSize: '14px'
  },
  error: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 12px',
    backgroundColor: 'rgba(239, 68, 68, 0.2)',
    borderRadius: '8px',
    color: '#fca5a5',
    fontSize: '13px'
  },
  dismissError: {
    background: 'none',
    border: 'none',
    color: '#fca5a5',
    cursor: 'pointer',
    fontSize: '16px',
    padding: '0 4px'
  },
  controlButtons: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px'
  },
  button: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 14px',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '500',
    transition: 'all 0.2s ease',
    fontFamily: '"Cinzel", serif',
    color: '#fff'
  },
  startButton: {
    backgroundColor: '#22c55e'
  },
  pauseButton: {
    backgroundColor: '#eab308'
  },
  resumeButton: {
    backgroundColor: '#22c55e'
  },
  endButton: {
    backgroundColor: '#ef4444'
  },
  emergencySaveButton: {
    backgroundColor: '#6366f1'
  },
  participantList: {
    marginTop: '8px',
    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
    paddingTop: '12px'
  },
  participantListTitle: {
    fontSize: '11px',
    color: 'rgba(255, 255, 255, 0.5)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    margin: '0 0 8px 0'
  },
  participantItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 0',
    fontSize: '13px'
  },
  participantRole: {
    fontSize: '12px'
  },
  participantName: {
    flex: 1,
    color: 'rgba(255, 255, 255, 0.9)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  disconnectedBadge: {
    fontSize: '11px',
    color: '#fca5a5',
    padding: '2px 6px',
    backgroundColor: 'rgba(239, 68, 68, 0.2)',
    borderRadius: '4px'
  }
};

/** CSS animation for auto-save pulse */
const saveAnimationStyles = `
  @keyframes save-pulse {
    0% { 
      opacity: 0.6; 
      filter: drop-shadow(0 0 0 rgba(74, 222, 128, 0)); 
    }
    50% { 
      opacity: 1; 
      filter: drop-shadow(0 0 8px rgba(74, 222, 128, 0.8)); 
    }
    100% { 
      opacity: 0.6; 
      filter: drop-shadow(0 0 0 rgba(74, 222, 128, 0)); 
    }
  }
  
  .saving {
    animation: save-pulse 1s ease-in-out;
  }
`;

export default SessionControls;

